// Pure formatting/parsing helpers for the funder and opportunity revision views.
// Extracted from the components so they can be unit-tested without rendering
// (and without tripping react-refresh's component-only-export rule).

export function formatDate(dateString: string | null): string {
  if (dateString === null) {
    return 'None'
  }
  const dateParts = dateString.split('-')
  return `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}`
}

// DeepDiff paths for a changed round field come in two shapes, depending on
// which write path created the revision:
//   - direct admin edit:      root[0]['funding_round']['estimate_date_flag']
//   - proposed-edit approval: root['funding_rounds'][1]['funding_round'][...]
// Pull out the round's list index and the leaf field that actually changed, so
// the revision view can name both instead of collapsing to "Funding Rounds".
export function roundIndexFromKey(key: string): number | null {
  const match =
    key.match(/\['funding_rounds'\]\[(\d+)\]/) || key.match(/^root\[(\d+)\]/)
  return match ? Number(match[1]) : null
}

export function leafFieldFromKey(key: string): string {
  const segments = Array.from(key.matchAll(/\['([^']+)'\]/g), (m) => m[1])
  return segments[segments.length - 1] ?? key
}

// Identify which round changed by its dates, read from the revision's stored
// snapshot. The diff path only carries the changed scalar, not its siblings —
// the snapshot is the only place the round's dates live. The list index is a
// best-effort match (DeepDiff runs with ignore_order), but for in-place field
// edits the position is stable.
export function describeRound(snapshot: any, index: number | null): string {
  if (index == null) return 'Funding round'
  // Rounds live under `funding_rounds` (current shape). Legacy direct-edit
  // revisions stored a bare list, which parse_json_field mislabels as
  // `region_ids` — fall back to it so historical revisions still resolve.
  const rounds = Array.isArray(snapshot)
    ? snapshot
    : (snapshot?.funding_rounds ?? snapshot?.region_ids)
  const round = rounds?.[index]?.funding_round
  if (!round) return 'Funding round'
  const open = round.application_open_date
    ? formatDate(round.application_open_date)
    : 'unknown'
  const close = round.application_close_date
    ? formatDate(round.application_close_date)
    : 'unknown'
  return `Funding round (opens ${open}, closes ${close})`
}

export interface ProvenanceRow {
  label: string
  value: string
}

// Human names for the `provenance.source` tags written by the fs-CLI direct
// write paths. Unknown tags fall through to the raw string rather than being
// hidden, so a new writer shows up in the trail the day it ships.
const PROVENANCE_SOURCE_LABELS: { [key: string]: string } = {
  'fs-set-check-in-funder': 'Direct check-in date write (funder)',
  'fs-set-check-in-opportunity': 'Direct check-in date write (opportunity)',
  'fs-direct-create': 'Direct create',
}

/**
 * Turn a revision's `provenance` blob into labelled rows for display (GH #1846).
 *
 * Provenance is metadata about the write, not a record field: why it happened,
 * what it replaced, and which automation did it. The direct check-in-date
 * writes are unreviewed, so this is the accountability record — rendering it
 * with JSON.stringify (the generic fallback) buries the reason in a blob.
 *
 * Returns null for anything that is not a provenance-shaped object, so callers
 * can fall back to their existing rendering.
 */
export function describeProvenance(value: unknown): ProvenanceRow[] | null {
  if (
    value === null ||
    typeof value !== 'object' ||
    Array.isArray(value)
  ) {
    return null
  }
  const p = value as { [key: string]: unknown }
  const rows: ProvenanceRow[] = []

  if (typeof p.reason === 'string' && p.reason !== '') {
    rows.push({ label: 'Reason', value: p.reason })
  }
  if ('previous_check_in_date' in p) {
    const previous = p.previous_check_in_date
    rows.push({
      label: 'Previous Check In Date',
      value: typeof previous === 'string' ? formatDate(previous) : 'None',
    })
  }
  if (
    typeof p.evidence_description === 'string' &&
    p.evidence_description !== ''
  ) {
    rows.push({ label: 'Evidence', value: p.evidence_description })
  }
  if (typeof p.evidence_url === 'string' && p.evidence_url !== '') {
    rows.push({ label: 'Evidence URL', value: p.evidence_url })
  }
  if (typeof p.source === 'string' && p.source !== '') {
    rows.push({
      label: 'Source',
      value: PROVENANCE_SOURCE_LABELS[p.source] || p.source,
    })
  }

  return rows.length > 0 ? rows : null
}

/**
 * Field name out of a DeepDiff "added item" entry (GH #1846).
 *
 * DeepDiff serialises `dictionary_item_added` as an array of path strings —
 * ["root['provenance']"] — not a field->value map. Object.entries() over that
 * yields index keys, which is why added items rendered as `0 field was added
 * with value: "root['provenance']"`. Returns null when the entry is not a
 * recognisable path, so callers can fall back.
 */
export function addedFieldName(path: unknown): string | null {
  if (typeof path !== 'string') return null
  const match = path.match(/^root\['([^']+)'\]$/)
  return match ? match[1] : null
}
